import api from '../api';
import type { IDiscussion, IReply } from '../../types/forum';// To type author data if needed

/**
 * Payload for creating a new discussion.
 */
interface CreateDiscussionPayload {
  title: string;
  content: string;
  category: string;
}

/**
 * Payload for adding a reply to a discussion.
 */
interface AddReplyPayload {
  content: string;
}

/**
 * Service function to get all forum discussions.
 * @returns A promise that resolves to an array of discussions.
 */
export const getAllDiscussions = async (): Promise<IDiscussion[]> => {
  try {
    const response = await api.get<IDiscussion[]>('/forum/discussions');
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch forum discussions.');
  }
};

/**
 * Service function to get a single forum discussion by ID.
 * @param id - The ID of the discussion.
 * @returns A promise that resolves to a single discussion.
 */
export const getDiscussionById = async (id: string): Promise<IDiscussion> => {
  try {
    const response = await api.get<IDiscussion>(`/forum/discussions/${id}`);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch discussion details.');
  }
};

/**
 * Service function to create a new forum discussion (Authenticated users only).
 * @param payload - Object containing title, content, and category.
 * @returns A promise that resolves with a success message and the new discussion.
 */
export const createDiscussion = async (payload: CreateDiscussionPayload): Promise<{ message: string; discussion: IDiscussion }> => {
  try {
    const response = await api.post<{ message: string; discussion: IDiscussion }>('/forum/discussions', payload);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to create discussion.');
  }
};

/**
 * Service function to add a reply to a discussion (Authenticated users only).
 * @param discussionId - The ID of the discussion to reply to.
 * @param payload - Object containing the reply content.
 * @returns A promise that resolves with a success message and the new reply.
 */
export const addReplyToDiscussion = async (discussionId: string, payload: AddReplyPayload): Promise<{ message: string; reply: IReply }> => {
  try {
    const response = await api.post<{ message: string; reply: IReply }>(`/forum/discussions/${discussionId}/reply`, payload);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to add reply to discussion.');
  }
};